import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addMessageToLifelog,
  addMessagesToLifelog,
  MAX_CHAT_PROMOTION_MESSAGES,
  loadMessageLifelogStates,
} from '../../services/messaging/messagePromotionService';
import { organizeCapturedCareSourceNote } from '../../features/rawCareCaptureExtraction/captureFirstExtractionService';

export const useMessageLifelogPromotion = ({ messages, conversation, child, userId }) => {
  const conversationId = conversation?.id;
  const [promotionError, setPromotionError] = useState(null);
  const [failedPromotionId, setFailedPromotionId] = useState(null);
  const [addingMessageId, setAddingMessageId] = useState(null);
  const [addedMessageIds, setAddedMessageIds] = useState(() => new Set());
  const [promotionStates, setPromotionStates] = useState({});
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState(() => new Set());
  const activeConversationIdRef = useRef(conversationId);
  activeConversationIdRef.current = conversationId;
  const promotionStatesRef = useRef(promotionStates);
  promotionStatesRef.current = promotionStates;

  const availableMessageIds = useMemo(() => new Set(messages.map((message) => message.id)), [messages]);
  const selectedMessages = useMemo(
    () => messages.filter((message) => selectedMessageIds.has(message.id) && !addedMessageIds.has(message.id)),
    [addedMessageIds, messages, selectedMessageIds]
  );

  useEffect(() => {
    setPromotionError(null);
    setFailedPromotionId(null);
    setAddingMessageId(null);
    setAddedMessageIds(new Set());
    setPromotionStates({});
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
  }, [conversationId]);

  useEffect(() => {
    const requestConversationId = conversationId;
    if (!requestConversationId || !messages.length || !userId) return undefined;
    let cancelled = false;
    let pollTimer = null;
    const loadStates = () => loadMessageLifelogStates({ messages, conversation: { id: requestConversationId } })
      .then((result) => {
        if (cancelled || activeConversationIdRef.current !== requestConversationId) return;
        const states = result?.promotions || {};
        setPromotionStates(states);
        setAddedMessageIds(new Set(Object.keys(states)));
        const hasProcessingSource = Object.values(states).some((state) => (
          ['captured', 'pending', 'organizing'].includes(state.processingStatus || state.status)
        ));
        if (hasProcessingSource) pollTimer = setTimeout(loadStates, 5000);
      })
      .catch((error) => {
        if (!cancelled && activeConversationIdRef.current === requestConversationId) {
          setPromotionError(error?.message || 'Lifelog status could not be loaded.');
          const wasProcessing = Object.values(promotionStatesRef.current).some((state) => (
            ['captured', 'pending', 'organizing'].includes(state.processingStatus || state.status)
          ));
          if (wasProcessing) pollTimer = setTimeout(loadStates, 10000);
        }
      });
    loadStates();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [conversationId, messages, statusRefreshKey, userId]);

  useEffect(() => {
    setSelectedMessageIds((current) => {
      const next = new Set([...current].filter((id) => availableMessageIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [availableMessageIds]);

  const addOne = useCallback(async (message) => {
    if (!child?.id || !userId || addingMessageId) return;
    const requestConversationId = conversationId;
    setAddingMessageId(message.id);
    setFailedPromotionId(null);
    setPromotionError(null);
    try {
      const result = await addMessageToLifelog({ message, conversation, child, userId });
      if (!result?.ok) throw new Error('The message could not be added.');
      if (activeConversationIdRef.current === requestConversationId) {
        setAddedMessageIds((current) => new Set([...current, message.id]));
        setPromotionStates((current) => ({ ...current, [message.id]: {
          sourceNoteId: result.sourceNoteId,
          status: result.status || 'captured',
          processingStatus: result.status || 'captured',
          reviewState: 'not_needed',
        } }));
        setStatusRefreshKey((current) => current + 1);
      }
    } catch (error) {
      if (activeConversationIdRef.current === requestConversationId) {
        setFailedPromotionId(message.id);
        setPromotionError(error?.message || 'The message could not be added to Lifelog.');
      }
    } finally {
      if (activeConversationIdRef.current === requestConversationId) setAddingMessageId(null);
    }
  }, [addingMessageId, child, conversation, conversationId, userId]);

  const toggleSelection = useCallback((message) => {
    if (addedMessageIds.has(message.id)) return;
    setFailedPromotionId((current) => current === 'message-set' ? null : current);
    setPromotionError(null);
    setSelectedMessageIds((current) => {
      const next = new Set(current);
      if (next.has(message.id)) next.delete(message.id);
      else if (next.size < MAX_CHAT_PROMOTION_MESSAGES) next.add(message.id);
      return next;
    });
  }, [addedMessageIds]);

  const retryOne = useCallback(async (message) => {
    const state = promotionStates[message.id];
    if (!state?.sourceNoteId || addingMessageId) return;
    const requestConversationId = conversationId;
    setAddingMessageId(message.id);
    setPromotionError(null);
    setPromotionStates((current) => ({ ...current, [message.id]: {
      ...state, status: 'organizing', processingStatus: 'organizing',
    } }));
    try {
      const result = await organizeCapturedCareSourceNote({
        sourceNoteId: state.sourceNoteId,
        options: { force: true, retry: true },
      });
      if (!result?.ok) throw new Error(result?.error?.message || 'Lifelog processing could not be retried.');
      if (activeConversationIdRef.current === requestConversationId) {
        setPromotionStates((current) => ({ ...current, [message.id]: {
          ...current[message.id], status: result.status || 'organizing', processingStatus: result.status || 'organizing',
        } }));
        setStatusRefreshKey((current) => current + 1);
      }
    } catch (error) {
      if (activeConversationIdRef.current === requestConversationId) {
        setPromotionStates((current) => ({ ...current, [message.id]: state }));
        setPromotionError(error?.message || 'Lifelog processing could not be retried.');
      }
    } finally {
      if (activeConversationIdRef.current === requestConversationId) setAddingMessageId(null);
    }
  }, [addingMessageId, conversationId, promotionStates]);

  const cancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
    setFailedPromotionId((current) => current === 'message-set' ? null : current);
    setPromotionError(null);
  }, []);

  const addSelected = useCallback(async () => {
    if (selectedMessages.length < 2 || addingMessageId) return;
    const requestConversationId = conversationId;
    setAddingMessageId('message-set');
    setFailedPromotionId(null);
    setPromotionError(null);
    try {
      const result = await addMessagesToLifelog({ messages: selectedMessages, conversation });
      if (!result?.ok) throw new Error('The selected messages could not be added.');
      if (activeConversationIdRef.current === requestConversationId) {
        setAddedMessageIds((current) => new Set([...current, ...selectedMessages.map((message) => message.id)]));
        setPromotionStates((current) => Object.fromEntries([
          ...Object.entries(current),
          ...selectedMessages.map((message) => [message.id, {
            sourceNoteId: result.sourceNoteId, status: result.status || 'captured', processingStatus: result.status || 'captured', reviewState: 'not_needed',
          }]),
        ]));
        setStatusRefreshKey((current) => current + 1);
        cancelSelection();
      }
    } catch (error) {
      if (activeConversationIdRef.current === requestConversationId) {
        setFailedPromotionId('message-set');
        setPromotionError(error?.message || 'The selected messages could not be added to Lifelog.');
      }
    } finally {
      if (activeConversationIdRef.current === requestConversationId) setAddingMessageId(null);
    }
  }, [addingMessageId, cancelSelection, conversation, conversationId, selectedMessages]);

  return {
    promotionError,
    clearPromotionError: () => setPromotionError(null),
    failedPromotionId,
    addingMessageId,
    addedMessageIds,
    promotionStates,
    selectionMode,
    beginSelection: () => setSelectionMode(true),
    selectedMessageIds,
    selectedCount: selectedMessages.length,
    selectionAtLimit: selectedMessages.length >= MAX_CHAT_PROMOTION_MESSAGES,
    addOne,
    retryOne,
    toggleSelection,
    cancelSelection,
    addSelected,
  };
};
